import * as React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { Stack } from '@fluentui/react';

import { PageNotFound } from './Components/PageNotFound';
import { ErrorBoundary } from './Components/ErrorBoundary/ErrorBoundary';
import { HRCDashboard } from './Pages/PBT/HRCDashboard/HRCDashboard';
import CRMCasesPage from './Pages/PBT/CRMCases/CRMCasesPage';
import CaseDetailPage from './Pages/PBT/CaseDetail/CaseDetailPage';
import { EmailTemplateAdmin } from './Containers/EmailTemplateAdmin/EmailTemplateAdmin';
import { useFeature, usePermission, useFeatureTreeLoading } from './Hooks/useFeature';
import { LoadingSpinner } from './Components/LoadingSpinner/LoadingSpinner';

interface IRoutesProps {
}

/** Renders children only if the capability is enabled; otherwise redirects away. */
const RouteGuard = ({ capability, children }: { capability: string; children: React.ReactNode }): React.ReactElement => {
  const { enabled } = useFeature(capability);
  const loading = useFeatureTreeLoading();
  if (loading) return <LoadingSpinner />;
  if (!enabled) return <Redirect to="/PBT/Dashboard" />;
  return <>{children}</>;
};

/** Renders children only if the fine-grained permission is granted; otherwise redirects away.
 *  Protects the route itself (direct URL access), not just the nav link. */
const PermissionRouteGuard = ({ permission, children }: { permission: string; children: React.ReactNode }): React.ReactElement => {
  const allowed = usePermission(permission);
  const loading = useFeatureTreeLoading();
  if (loading) return <LoadingSpinner />;
  if (!allowed) return <Redirect to="/PBT/Dashboard" />;
  return <>{children}</>;
};

export const Routes = (_props: IRoutesProps): React.ReactElement => {
  const defaultPath = '/PBT/Dashboard';
  return (
    <Switch>
        <Redirect exact from="/" to={defaultPath} />
        <Route path="/PBT/Dashboard" render={() => (
          <ErrorBoundary featureName="HRC Dashboard">
            <RouteGuard capability="page.hrcDashboard">
              <HRCDashboard />
            </RouteGuard>
          </ErrorBoundary>
        )} exact />
        <Route path="/PBT/CRMCases" render={() => (
          <ErrorBoundary featureName="CRM Cases">
            <RouteGuard capability="page.crmCases">
              <CRMCasesPage />
            </RouteGuard>
          </ErrorBoundary>
        )} exact />
        <Route path="/PBT/Cases/:id" render={() => (
          <ErrorBoundary featureName="Case Detail">
            <CaseDetailPage />
          </ErrorBoundary>
        )} exact />
        <Route path="/email-templates" render={() => (
          <ErrorBoundary featureName="Email Templates">
            <PermissionRouteGuard permission="Email.ManageTemplates">
              <EmailTemplateAdmin />
            </PermissionRouteGuard>
          </ErrorBoundary>
        )} exact />
        <Route render={() => (
          <Stack className={'Container'}>
            <PageNotFound title="Page Not Found" message="The page you are looking for does not exist." iconName="ErrorBadge" />
          </Stack>
        )} />
    </Switch>
  );
}